import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Download,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2,
  X,
  ArrowRight,
  Sparkles,
  ChevronDown,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = {
  INTRO: 'intro',
  PREVIEW: 'preview',
  RESULT: 'result',
};

const downloadTemplate = async () => {
  const t = toast.loading('Preparing template…');
  try {
    const res = await fetch(`${API_URL}/api/tasks/bulk/template.xlsx`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to download template');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'prism-bulk-tasks-template.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded — open it in Excel', { id: t });
  } catch (e) {
    toast.error(e.message || 'Could not download template', { id: t });
  }
};

const StatusPill = ({ ok, hasWarnings }) => {
  if (!ok) {
    return (
      <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
        <AlertCircle className="h-3 w-3" /> Invalid
      </span>
    );
  }
  if (hasWarnings) {
    return (
      <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
        <AlertTriangle className="h-3 w-3" /> Warning
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
      <CheckCircle2 className="h-3 w-3" /> Ready
    </span>
  );
};

const RowCard = ({ row, expandedRows, setExpandedRows }) => {
  const isOpen = expandedRows.has(row.row);
  const hasWarnings = (row.warnings || []).length > 0;
  const hasIssues = (row.errors || []).length > 0 || hasWarnings;
  const d = row.data || {};

  return (
    <div
      className={`border rounded-xl overflow-hidden transition-colors ${
        row.ok
          ? hasWarnings
            ? 'border-amber-200 bg-amber-50/40'
            : 'border-emerald-200 bg-emerald-50/30'
          : 'border-rose-200 bg-rose-50/40'
      }`}
      data-testid={`bulk-row-${row.row}`}
    >
      <button
        type="button"
        onClick={() => {
          const n = new Set(expandedRows);
          n.has(row.row) ? n.delete(row.row) : n.add(row.row);
          setExpandedRows(n);
        }}
        className="w-full px-4 py-3 flex items-center gap-3 text-left"
      >
        <span className="text-[10px] font-mono font-bold text-slate-400 w-8 shrink-0">
          #{row.row}
        </span>
        <StatusPill ok={row.ok} hasWarnings={hasWarnings} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-800 truncate">
            {d.brand_name || <span className="italic text-slate-400">No brand</span>}
            <span className="text-slate-300 mx-1.5">·</span>
            <span className="text-slate-600 font-normal">
              {d.task_type || <span className="italic text-slate-400">No type</span>}
            </span>
            {d.task_kind && (
              <span className="ml-2 text-[10px] uppercase font-bold tracking-wider text-slate-500">
                {d.task_kind === 'content' ? '✍ Content' : '🎨 Design'}
              </span>
            )}
          </div>
          <div className="text-[11.5px] text-slate-500 truncate mt-0.5">
            {d.required_by_date && (
              <span className="font-medium">📅 {d.required_by_date}</span>
            )}
            {d.submitter_email && (
              <>
                <span className="text-slate-300 mx-1.5">·</span>
                <span>{d.submitter_email}</span>
              </>
            )}
            {!d.brand_name && !d.task_type && row.errors?.length === 0 && (
              <span className="italic">Empty row</span>
            )}
          </div>
        </div>
        {hasIssues && (
          <span className="text-[10.5px] font-bold text-slate-500">
            {row.errors?.length || 0}E / {row.warnings?.length || 0}W
          </span>
        )}
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
        )}
      </button>

      {isOpen && (
        <div className="px-4 pb-3 pt-1 border-t border-slate-200/60 bg-white/60">
          {row.errors?.length > 0 && (
            <div className="mb-2">
              <div className="text-[10.5px] font-bold uppercase tracking-wider text-rose-600 mb-1">
                Errors
              </div>
              <ul className="space-y-1">
                {row.errors.map((e, i) => (
                  <li key={i} className="text-[12px] text-rose-700 flex items-start gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {row.warnings?.length > 0 && (
            <div className="mb-2">
              <div className="text-[10.5px] font-bold uppercase tracking-wider text-amber-600 mb-1">
                Warnings
              </div>
              <ul className="space-y-1">
                {row.warnings.map((w, i) => (
                  <li key={i} className="text-[12px] text-amber-700 flex items-start gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-2 text-[11.5px]">
            {d.dimensions && (
              <div>
                <span className="text-slate-400 uppercase tracking-wider text-[10px] font-bold">
                  Dimensions:{' '}
                </span>
                <span className="text-slate-700">{d.dimensions}</span>
              </div>
            )}
            {Array.isArray(d.featured_products) && d.featured_products.length > 0 && (
              <div className="col-span-2">
                <span className="text-slate-400 uppercase tracking-wider text-[10px] font-bold">
                  SKUs:{' '}
                </span>
                <span className="text-slate-700">
                  {(Array.isArray(d.featured_products_names) && d.featured_products_names.length === d.featured_products.length
                    ? d.featured_products_names
                    : d.featured_products
                  ).join(', ')}
                </span>
              </div>
            )}
            {Array.isArray(d.references) && d.references.length > 0 && (
              <div className="col-span-2">
                <span className="text-slate-400 uppercase tracking-wider text-[10px] font-bold">
                  References:{' '}
                </span>
                <span className="text-slate-700">
                  {d.references
                    .map((r) => `${r.type === 'text' ? '✎' : '↗'} ${r.value}`)
                    .join(' · ')}
                </span>
              </div>
            )}
            {d.design_brief && (
              <div className="col-span-2">
                <span className="text-slate-400 uppercase tracking-wider text-[10px] font-bold">
                  Brief:{' '}
                </span>
                <span className="text-slate-700 line-clamp-2">{d.design_brief}</span>
              </div>
            )}
            {Array.isArray(d.notification_emails) && d.notification_emails.length > 0 && (
              <div className="col-span-2">
                <span className="text-slate-400 uppercase tracking-wider text-[10px] font-bold">
                  Notify:{' '}
                </span>
                <span className="text-slate-700">{d.notification_emails.join(', ')}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function BulkImportTasksModal({ open, onOpenChange, onCommitted }) {
  const [step, setStep] = useState(STEPS.INTRO);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [filterMode, setFilterMode] = useState('all'); // all | valid | invalid | warning
  const fileInputRef = useRef(null);

  const reset = () => {
    setStep(STEPS.INTRO);
    setFile(null);
    setPreviewData(null);
    setResult(null);
    setExpandedRows(new Set());
    setFilterMode('all');
  };

  const handleClose = (val) => {
    if (!val) reset();
    onOpenChange(val);
  };

  const handleFile = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Please upload an .xlsx file');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max 10MB.');
      return;
    }
    setFile(f);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(`${API_URL}/api/tasks/bulk/preview`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.detail || 'Preview failed');
      }
      setPreviewData(json);
      // Auto-expand first invalid row to surface issues
      const firstInvalid = (json.rows || []).find((r) => !r.ok);
      if (firstInvalid) setExpandedRows(new Set([firstInvalid.row]));
      setStep(STEPS.PREVIEW);
      if (json.invalid > 0) {
        toast.warning(`${json.invalid} of ${json.total} rows have errors`);
      } else {
        toast.success(`${json.valid} rows ready to import`);
      }
    } catch (e) {
      toast.error(e.message || 'Could not parse template');
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleCommit = async () => {
    if (!previewData) return;
    const validRows = previewData.rows.filter((r) => r.ok);
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }
    setCommitting(true);
    try {
      const res = await fetch(`${API_URL}/api/tasks/bulk/commit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rows: validRows }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Commit failed');
      setResult(json);
      setStep(STEPS.RESULT);
      toast.success(`${json.created_count} task${json.created_count === 1 ? '' : 's'} created`);
      onCommitted?.(json);
    } catch (e) {
      toast.error(e.message || 'Import failed');
    } finally {
      setCommitting(false);
    }
  };

  const filteredRows = (previewData?.rows || []).filter((r) => {
    if (filterMode === 'all') return true;
    if (filterMode === 'valid') return r.ok && (r.warnings || []).length === 0;
    if (filterMode === 'warning') return r.ok && (r.warnings || []).length > 0;
    if (filterMode === 'invalid') return !r.ok;
    return true;
  });

  const filterCounts = {
    all: previewData?.total || 0,
    valid:
      (previewData?.rows || []).filter((r) => r.ok && (r.warnings || []).length === 0).length || 0,
    warning: (previewData?.rows || []).filter((r) => r.ok && (r.warnings || []).length > 0).length || 0,
    invalid: previewData?.invalid || 0,
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] flex flex-col p-0 overflow-hidden"
        data-testid="bulk-import-modal"
      >
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50/50 shrink-0">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-md shadow-violet-200">
              <FileSpreadsheet className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
                Bulk Import Creative Tasks
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200">
                  Meta
                </span>
              </DialogTitle>
              <DialogDescription className="text-[12.5px] text-slate-500 mt-0.5">
                Upload up to 50 tasks at once via Excel. Designed for Meta platform tasks only.
              </DialogDescription>
            </div>
          </div>

          {/* Steps */}
          <div className="flex items-center gap-2 mt-4">
            {[
              { key: STEPS.INTRO, label: '1. Download & Upload' },
              { key: STEPS.PREVIEW, label: '2. Review' },
              { key: STEPS.RESULT, label: '3. Done' },
            ].map((s, i) => (
              <React.Fragment key={s.key}>
                <div
                  className={`text-[10.5px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full transition-colors ${
                    step === s.key
                      ? 'bg-violet-600 text-white'
                      : Object.values(STEPS).indexOf(step) > i
                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                      : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {s.label}
                </div>
                {i < 2 && <div className="h-px flex-1 bg-slate-200" />}
              </React.Fragment>
            ))}
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === STEPS.INTRO && (
            <div className="space-y-5">
              <div className="rounded-xl border border-violet-100 bg-violet-50/40 p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-4 w-4 text-violet-600 mt-0.5 shrink-0" />
                  <div className="text-[12.5px] text-slate-700 leading-relaxed">
                    <strong className="text-slate-900">How it works:</strong> Download the template
                    below — it includes live dropdowns for brands, task types, SKUs, and assignees
                    pulled from your masters. Fill in up to 50 rows, upload it back here, and we'll
                    validate every row before creating the tasks.
                  </div>
                </div>
              </div>

              {/* Step 1: Download */}
              <div className="rounded-xl border border-slate-200 p-4 bg-white">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">
                      Step 1
                    </div>
                    <div className="text-[14px] font-semibold text-slate-900 mt-0.5">
                      Download the Excel template
                    </div>
                    <div className="text-[12px] text-slate-500 mt-0.5">
                      Includes live dropdowns for Brand, Platform, Task Kind, Task Type and SKU.
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={downloadTemplate}
                    className="bg-violet-600 hover:bg-violet-700 text-white shrink-0"
                    data-testid="bulk-download-template-btn"
                  >
                    <Download className="h-4 w-4 mr-1.5" /> Download .xlsx
                  </Button>
                </div>
              </div>

              {/* Step 2: Upload */}
              <div
                className={`rounded-xl border-2 border-dashed transition-colors p-6 text-center bg-white ${
                  uploading
                    ? 'border-violet-300 bg-violet-50/40'
                    : 'border-slate-200 hover:border-violet-300 hover:bg-violet-50/20'
                }`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
              >
                <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                  Step 2
                </div>
                {uploading ? (
                  <div className="flex flex-col items-center gap-2 py-2">
                    <Loader2 className="h-7 w-7 text-violet-600 animate-spin" />
                    <div className="text-[13px] text-slate-700 font-medium">
                      Validating {file?.name}…
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="h-7 w-7 text-slate-400 mx-auto mb-2" />
                    <div className="text-[14px] font-semibold text-slate-900">
                      Drag your filled template here
                    </div>
                    <div className="text-[12px] text-slate-500 mt-0.5 mb-3">
                      or click below to browse · .xlsx only · max 10MB · max 50 rows
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx"
                      className="hidden"
                      onChange={(e) => handleFile(e.target.files?.[0])}
                      data-testid="bulk-upload-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="bulk-upload-browse-btn"
                    >
                      <Upload className="h-4 w-4 mr-1.5" /> Choose file
                    </Button>
                  </>
                )}
              </div>

              {/* Notes — mirror the manual Submit Task form */}
              <div className="text-[11.5px] text-slate-500 space-y-1.5 leading-relaxed">
                <div>
                  <strong className="text-slate-700">Always required:</strong> brand, task_kind
                  (design / content), task_type, design_brief (≥10 chars), references (at least one),
                  required_by_date (YYYY-MM-DD), submitter_email, at least one notification_email.
                </div>
                <div>
                  <strong className="text-slate-700">Design only — also required:</strong>{' '}
                  dimensions, featured_skus (at least one).
                </div>
                <div>
                  <strong className="text-slate-700">Content only — optional:</strong> dimensions,
                  featured_skus. References can be free-form text instead of links.
                </div>
                <div>
                  <strong className="text-slate-700">Always optional:</strong> cc_emails,
                  product_creatives.
                </div>
                <div>
                  <strong className="text-slate-700">References format:</strong> pipe-separated.
                  Each item supports the manual-form link/text toggle —{' '}
                  <code className="px-1 py-0.5 rounded bg-slate-100">
                    link:https://example.com | text:Match the September voice guide
                  </code>
                  . Bare values default to link.
                </div>
                <div>
                  <strong className="text-slate-700">product_creatives:</strong> optional Meta
                  helper — pipe-separated{' '}
                  <code className="px-1 py-0.5 rounded bg-slate-100">Product Name:count</code>{' '}
                  pairs (e.g.{' '}
                  <code className="px-1 py-0.5 rounded bg-slate-100">
                    Whey Protein:3 | Creatine:1
                  </code>
                  ).
                </div>
                <div>
                  <strong className="text-slate-700">featured_skus:</strong> dropdown shows product
                  names — pick from the list or paste multiple, pipe-separated. Raw SKU IDs are
                  still accepted for back-compat.
                </div>
                <div>
                  <strong className="text-slate-700">Platform:</strong> locked to{' '}
                  <code className="px-1 py-0.5 rounded bg-slate-100">Meta</code> for every row.
                </div>
              </div>
            </div>
          )}

          {step === STEPS.PREVIEW && previewData && (
            <div className="space-y-4">
              {/* Summary strip */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: 'Total', value: previewData.total, color: 'slate' },
                  { label: 'Ready', value: filterCounts.valid, color: 'emerald' },
                  { label: 'With warnings', value: filterCounts.warning, color: 'amber' },
                  { label: 'Invalid', value: filterCounts.invalid, color: 'rose' },
                ].map((s) => (
                  <div
                    key={s.label}
                    className={`rounded-xl border p-3 ${
                      s.color === 'emerald'
                        ? 'border-emerald-200 bg-emerald-50/40'
                        : s.color === 'amber'
                        ? 'border-amber-200 bg-amber-50/40'
                        : s.color === 'rose'
                        ? 'border-rose-200 bg-rose-50/40'
                        : 'border-slate-200 bg-slate-50/40'
                    }`}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      {s.label}
                    </div>
                    <div
                      className={`text-2xl font-bold ${
                        s.color === 'emerald'
                          ? 'text-emerald-700'
                          : s.color === 'amber'
                          ? 'text-amber-700'
                          : s.color === 'rose'
                          ? 'text-rose-700'
                          : 'text-slate-800'
                      }`}
                    >
                      {s.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Filter pills */}
              <div className="flex flex-wrap items-center gap-1.5">
                {[
                  { key: 'all', label: `All (${filterCounts.all})` },
                  { key: 'valid', label: `Ready (${filterCounts.valid})` },
                  { key: 'warning', label: `Warnings (${filterCounts.warning})` },
                  { key: 'invalid', label: `Invalid (${filterCounts.invalid})` },
                ].map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilterMode(f.key)}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                      filterMode === f.key
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`bulk-filter-${f.key}`}
                  >
                    {f.label}
                  </button>
                ))}
                <div className="ml-auto text-[11px] text-slate-400">
                  Next ID:{' '}
                  <span className="font-mono font-bold text-violet-700">
                    {previewData.next_task_id || '—'}
                  </span>
                </div>
              </div>

              {/* Row list */}
              <div className="space-y-2">
                {filteredRows.length === 0 && (
                  <div className="text-center py-8 text-[12.5px] text-slate-400">
                    No rows match this filter.
                  </div>
                )}
                {filteredRows.map((row) => (
                  <RowCard
                    key={row.row}
                    row={row}
                    expandedRows={expandedRows}
                    setExpandedRows={setExpandedRows}
                  />
                ))}
              </div>
            </div>
          )}

          {step === STEPS.RESULT && result && (
            <div className="space-y-5">
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-3">
                  <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                </div>
                <div className="text-2xl font-bold text-slate-900">
                  {result.created_count} task{result.created_count === 1 ? '' : 's'} created
                </div>
                {result.failed_count > 0 && (
                  <div className="text-[12.5px] text-rose-600 mt-1">
                    {result.failed_count} row{result.failed_count === 1 ? '' : 's'} failed to import
                  </div>
                )}
              </div>

              {result.created?.length > 0 && (
                <div>
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Created tasks
                  </div>
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    {result.created.map((c) => (
                      <div
                        key={c.task_id}
                        className="px-4 py-2.5 flex items-center gap-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60"
                      >
                        <span className="font-mono text-[12px] font-bold text-violet-700 shrink-0">
                          {c.task_id}
                        </span>
                        <span className="text-[12.5px] text-slate-700 font-medium truncate">
                          {c.brand_name}
                        </span>
                        <span className="text-slate-300">·</span>
                        <span className="text-[12.5px] text-slate-600 truncate flex-1">
                          {c.task_type}
                        </span>
                        <span className="text-[11px] text-slate-400 shrink-0">
                          {c.required_by_date}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.failed?.length > 0 && (
                <div>
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-rose-600 mb-2">
                    Failed rows
                  </div>
                  <div className="border border-rose-200 rounded-xl overflow-hidden bg-rose-50/30">
                    {result.failed.map((f, i) => (
                      <div
                        key={i}
                        className="px-4 py-2 flex items-center gap-3 border-b border-rose-100 last:border-b-0"
                      >
                        <span className="font-mono text-[11px] font-bold text-rose-700 shrink-0">
                          #{f.row}
                        </span>
                        <span className="text-[12px] text-rose-700 truncate">{f.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between gap-3 shrink-0">
          <div className="text-[11px] text-slate-500">
            {step === STEPS.PREVIEW && file?.name && (
              <span>
                <FileSpreadsheet className="h-3 w-3 inline mr-1 -mt-0.5" />
                {file.name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === STEPS.INTRO && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleClose(false)}
                data-testid="bulk-cancel-btn"
              >
                Cancel
              </Button>
            )}
            {step === STEPS.PREVIEW && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={reset}
                  disabled={committing}
                  data-testid="bulk-start-over-btn"
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  Start over
                </Button>
                <Button
                  type="button"
                  onClick={handleCommit}
                  disabled={committing || filterCounts.valid + filterCounts.warning === 0}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="bulk-commit-btn"
                >
                  {committing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      Importing…
                    </>
                  ) : (
                    <>
                      Import {filterCounts.valid + filterCounts.warning} task
                      {filterCounts.valid + filterCounts.warning === 1 ? '' : 's'}
                      <ArrowRight className="h-4 w-4 ml-1.5" />
                    </>
                  )}
                </Button>
              </>
            )}
            {step === STEPS.RESULT && (
              <Button
                type="button"
                onClick={() => handleClose(false)}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="bulk-done-btn"
              >
                Done
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
